import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import type { MitLimits, PublicMitMandateDTO } from '../../../core/models';
import { MIT_LIMIT_ROWS, MitLimitRow, MitService, mitApiError, mitLimitValue } from '../../../services/mit.service';
import { PublicMitShellComponent } from '../public-mit-shell/public-mit-shell.component';
import { collectDeviceEvidence } from '../../../utils/device-evidence';

type LimitsView = 'loading' | 'invalid' | 'none' | 'form' | 'accepted';

interface ComparedRow {
  row: MitLimitRow;
  current: string;
  proposed: string;
  raised: boolean;
}

@Component({
  selector: 'app-public-mit-limits',
  templateUrl: './public-mit-limits.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, PublicMitShellComponent]
})
export class PublicMitLimitsComponent {
  private route = inject(ActivatedRoute);
  private mitService = inject(MitService);

  private id = Number(this.route.snapshot.paramMap.get('id'));
  private hash = this.route.snapshot.queryParamMap.get('hash');

  view = signal<LimitsView>('loading');
  mandate = signal<PublicMitMandateDTO | null>(null);
  accepted = signal(false);
  sending = signal(false);
  error = signal<string | null>(null);

  rows = computed<ComparedRow[]>(() => {
    const m = this.mandate();
    if (!m?.pendingLimits) return [];
    return MIT_LIMIT_ROWS
      .filter((row) => row.key !== 'maxConsecutiveDeclines' && (m.limits?.[row.key] != null || m.pendingLimits?.[row.key] != null))
      .map((row) => ({
        row,
        current: mitLimitValue(row, m.limits),
        proposed: mitLimitValue(row, m.pendingLimits),
        raised: this.raises(row, m.limits, m.pendingLimits)
      }));
  });
  newRows = computed(() => {
    const limits = this.mandate()?.limits;
    return MIT_LIMIT_ROWS
      .filter((row) => row.key !== 'maxConsecutiveDeclines' && limits?.[row.key] != null)
      .map((row) => ({ label: row.label, value: mitLimitValue(row, limits) }));
  });

  constructor() {
    if (!this.id || !this.hash) {
      this.view.set('invalid');
      return;
    }
    this.mitService.getPublic(this.id, this.hash).subscribe({
      next: (response) => {
        const mandate = response.body ?? null;
        this.mandate.set(mandate);
        this.view.set(!mandate ? 'invalid' : mandate.pendingLimits ? 'form' : 'none');
      },
      error: () => this.view.set('invalid')
    });
  }

  async accept(): Promise<void> {
    const mandate = this.mandate();
    if (!mandate || !this.accepted() || this.sending()) return;
    this.sending.set(true);
    this.error.set(null);
    const device = await collectDeviceEvidence();
    this.mitService.consentPublic(this.id, this.hash!, {
      consentAccepted: true,
      termsVersion: mandate.termsVersion ?? '',
      device
    }).subscribe({
      next: (response) => {
        this.sending.set(false);
        if (response.body) this.mandate.set(response.body);
        this.view.set('accepted');
      },
      error: (err: HttpErrorResponse) => {
        this.sending.set(false);
        if (err.status === 403) {
          this.view.set('invalid');
          return;
        }
        const { message, code } = mitApiError(err, 'No se pudieron aceptar los nuevos límites');
        if (code === 'NO_PENDING_LIMITS') {
          this.view.set('none');
          return;
        }
        this.error.set(message);
      }
    });
  }

  /** Un límite «sube» si el propuesto es mayor o si deja de tener tope; el mínimo sube cuando baja. */
  private raises(row: MitLimitRow, current: MitLimits | undefined, proposed: MitLimits | undefined): boolean {
    const before = current?.[row.key];
    const after = proposed?.[row.key];
    if (before === after) return false;
    if (row.key === 'minAmountPerTransaction' || row.key === 'minIntervalSeconds') {
      return before != null && (after == null || after < before);
    }
    return before != null && (after == null || after > before);
  }
}
